import { Odr, OdrRsf } from '../../../catalog/products/interface/phone';
import { IPromotions } from '../../../checkout/cart/promotions/promotion.interface';
import { CustomerContextService } from '../../../context/child/customer-context.service';
import { RenewContextService } from '../../../context/child/renew-context.service';
import { SchemeBase } from '../../checkout/cart/scheme.class';
import { ProductContextBase } from '../../context/child/product-context.service';
import { AttributeBase, ProductSerializedBase } from './interface/configurable';
import { EdpData, JsonProduct } from './interface/context';

export enum PriceTypes {
    Today,
    EvryMonth,
    Other,
}

export interface RulesRenew {
    planType?: string[];
}

export abstract class ProductBase {
    public gencode: string;
    public id: number;
    public price: number;
    public oldPrice: number;
    public alonePrice: number;
    public specialPrice: number;
    public realOldPrice: number;
    public specialOldPrice?: number;
    public baseprice: number;
    public priceType: PriceTypes = PriceTypes.Today;
    public childs: string[];
    public manufacturer: string;
    public manufacturer_code: string;
    public name: string;
    public dataEnvelope: string;
    public prices: any;
    public data: JsonProduct;
    public medias: string[] = [];
    public qty: number = 1;
    public uniqueId: string;
    public duration: number = 0;
    public title: string = null;
    public obligation: string;
    public odrRsf: OdrRsf;
    public locked: string;
    public color?: string;
    public capacity?: string;
    public sku?: string;
    public type_id?: string;
    public type?: string;
    public stock?: any;
    public in_stock: boolean;
    public exclu_web?: boolean;
    public is_darwin?: boolean;
    public promotion: IPromotions[] = [];
    public porta: IPromotions[] = [];
    public convergence: IPromotions[] = [];
    public locales: IPromotions[] = [];
    public customer: IPromotions[] = [];
    public forcedCustomer: IPromotions[] = [];
    public hasIncitation: boolean = false;
    public discountPercent?: number;
    public bonus_recovery?: string;
    public recovery_from_date?: string;
    public recovery_to_date?: string;

    private _forcedCustomerPromo: boolean = false;

    protected _context: ProductContextBase;
    protected _scanCode: string = '';

    get context(): ProductContextBase {
        return this._context;
    }
    set context(theContext: ProductContextBase) {
        this._context = theContext;
        this.updatePrice();
    }
    get customerContext(): CustomerContextService {
        return this.context.contextService.get<CustomerContextService>(CustomerContextService);
    }

    constructor(data: JsonProduct, uniqueId: string = '') {
        this.uniqueId = uniqueId === '' ? Date.now() + '-' + Math.floor(Math.random() * 1000) : uniqueId;
        this.data = data;
        this.id = data.entity_id;
        this.gencode = data.gencode;
        this.prices = data.prices;
        this.childs = data.product_ids;
        this.manufacturer = data.manufacturer;
        this.manufacturer_code = data.manufacturer_code;
        this.name = this.nameContextualisation(data.name);
        this.dataEnvelope = data.data_envelope;
        this.title = data.title;
        this.specialPrice = data.special_price;
        this.price = Number(data.price);
        this.medias = data.medias;
        this.oldPrice = Number.parseFloat(this.getData('final_price'));
        this.baseprice = this.oldPrice;
        this.obligation = data.obligation_id !== 'none' ? data.obligation : null;
        this.locked = data.locked;
        this.type_id = data.type_id;
        this.discountPercent = data.discount_percent;
        this.bonus_recovery = data.bonus_recovery;
        this.recovery_from_date = data.recovery_from_date;
        this.recovery_to_date = data.recovery_to_date;
        this.is_darwin = data?.is_darwin;
    }

    public getJsonData(): JsonProduct {
        return this.data;
    }

    private nameContextualisation(name: string): string {
        if (typeof name === 'string') {
            name = name.replace(/ +/g, ' ').trim();
        }
        return name;
    }

    public getData(key: string): any {
        return this.data[key];
    }

    public getEdpData(): EdpData {
        const renewContextService: RenewContextService =
            this.context.contextService.get<RenewContextService>(RenewContextService);
        const idPlan = this.context.plan;
        const plan = this.data.prices.plans[idPlan];

        if (renewContextService && renewContextService.edpPhone && !plan) {
            return this.data.edp_phone;
        } else {
            return plan ? plan['edp'] : null;
        }
    }

    public getPrice(plan?: string): number {
        return this.price;
    }

    public getEdp(plan: string): EdpData {
        return {};
    }

    public hasEdp(scheme: SchemeBase): boolean {
        return false;
    }

    public getColor(): AttributeBase {
        return {};
    }

    public getCapacity(): AttributeBase {
        return {};
    }

    public get odr(): Odr {
        return this.getData('odr');
    }

    public serialize(): ProductSerializedBase {
        return {
            uniqueId: this.uniqueId,
            gencode: this.gencode,
            price: this.price,
            quoteItem: {
                attributes: false,
                options: false,
                product: this.id,
                qty: 1,
                redirectPending: false,
                selectedPayment: 1,
            },
        };
    }

    /**
     * On add product in cart, this function is called to update productContext of products and scheme
     * We can upgrade this function with Before and After
     * @param productContext
     * @returns
     */
    public updateSchemeOnAdd(scheme: SchemeBase): SchemeBase {
        return scheme;
    }

    /**
     * On remove product in cart, this function is called to update productContext of products and scheme
     * We can upgrade this function with Befor and After
     * @param productContext
     * @returns
     */
    public updateSchemeOnRemove(scheme: SchemeBase): SchemeBase {
        return scheme;
    }

    protected updatePrice(): void {
        // extended in children
    }

    /**
     *
     * @protected
     * @returns
     * @memberof Product
     */
    protected updatePromotion(): void {
        // extended in children
    }

    /**
     * Return total of promotions
     * @param promotions
     * @returns Number
     */
    private sumTotal(promotions: IPromotions[]): number {
        if (!promotions) {
            return 0;
        }
        return promotions.reduce((acc, current) => {
            if (!acc) {
                return current.reduction;
            }
            return acc + current.reduction;
        }, 0);
    }

    /**
     * Return Min or Max from a range
     * @param promotions
     * @returns Number
     */
    private getMinMaxDuration(promotions: IPromotions[], level: string = 'min'): number {
        if (!promotions || !promotions.length) {
            return 0;
        }
        return Math[level].apply(
            null,
            promotions.map((promo) => promo.duree),
        );
    }

    /**
     * @getter TotalFai
     * @returns Number
     */
    public get promoTotalFai(): number {
        return this.sumTotal(this.promotion);
    }

    /**
     * @getter TOTAL
     * @returns Number
     */
    public get promosTotal(): number {
        if (this.customerContext == null || this.customerContext.contracts.length === 0) {
            return this.sumTotal(this.promotion);
        }
        return 0;
    }

    /**
     * @getter TOTAL
     * @returns Number
     */
    public get promosPortaTotal(): number {
        return this.sumTotal(this.porta);
    }

    /**
     * @getter TOTAL
     * @returns Number
     */
    public get promosLocaleTotal(): number {
        return this.sumTotal(this.locales);
    }

    /**
     * @returns Number
     */
    public promosConvergenceTotal(withIncitation: boolean): number {
        const condition = withIncitation ? this.obligation && this.hasIncitation : false;
        let convergence = this.convergence;
        if (convergence && this.customerContext != null) {
            convergence = convergence.filter(
                (promo: IPromotions) =>
                    (promo.conditions.callOperator && promo.produits.includes(this.data.gencode.toString())) ||
                    promo.hasContractType ||
                    condition,
            );
        }
        return this.sumTotal(convergence);
    }

    /**
     * @returns Number
     */
    public promosFakeConvergenceTotal(): number {
        let convergence;
        if (this.convergence) {
            convergence = this.convergence.filter(
                (promo: IPromotions) =>
                    !promo.conditions.catalogOperator && promo.produits.includes(this.data.gencode.toString()),
            );
        }
        return this.sumTotal(convergence);
    }

    /**
     * @getter TOTAL
     * @returns Number
     */
    public get promosCustomerTotal(): number {
        let customer = this.customer;
        if (customer && this.customerContext != null) {
            customer = customer.filter(
                (promo: IPromotions) =>
                    (promo.conditions.callOperator && promo.produits.includes(this.data.gencode.toString())) ||
                    promo.hasContractType ||
                    (this.obligation && promo.conditions.client.incitation),
            );
        }
        return this.sumTotal(customer);
    }

    public set forcedCustomerPromo(value: boolean) {
        this._forcedCustomerPromo = value;
    }

    public get forcedCustomerPromo(): boolean {
        return this._forcedCustomerPromo;
    }

    public isPlanFaim() {
        return false;
    }

    /**
     * @getter TOTAL
     * @returns Number
     */
    public get sowoPromosCustomerTotal(): number {
        let customer = this.customer;
        if (customer && this.customerContext != null) {
            customer = customer.filter((promo: IPromotions) => promo.hasContractType);
            return this.sumTotal(customer);
        }
        return 0;
    }

    public get promosClientTotal(): number {
        let customer = this.customer;

        if (customer && this.customerContext != null && this.customerContext.contracts.length > 0) {
            customer = customer.filter((promo: IPromotions) => promo.hasContractType);
            return this.sumTotal(customer);
        }
        return 0;
    }

    /**
     * @getter DURATION
     * @returns Number
     */
    public get promosDuration(): number {
        return this.getMinMaxDuration(this.promotion);
    }

    /**
     * @getter DURATION
     * @returns Number
     */
    public get promosPortaDuration(): number {
        return this.getMinMaxDuration(this.porta);
    }

    /**
     * @getter DURATION
     * @returns Number
     */
    public get promosLocaleDuration(): number {
        return this.getMinMaxDuration(this.locales);
    }

    /**
     * @getter DURATION
     * @returns Number
     */
    public get promosConvergenceDuration(): number {
        return this.getMinMaxDuration(this.convergence);
    }

    /**
     * @getter DURATION
     * @returns Number
     */
    public get promosCustomerDuration(): number {
        return this.getMinMaxDuration(this.customer);
    }
}
